import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MagnifyingGlass, ToggleLeft, ToggleRight, PlusCircle, MinusCircle, Trash, SealCheck, CaretLeft, CaretRight, Envelope, DownloadSimple, MouseSimple, Globe, Flag, Diamond, Heart, Ticket, X } from "@phosphor-icons/react";
import UserBadge from "@/components/UserBadge";
import AdminUserSitesHoverCard from "@/components/AdminUserSitesHoverCard";

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

function UserAvatar({ user, size = 32 }) {
  const src = resolveImg(user?.avatar_url);
  const letter = (user?.username || "?").slice(0, 1).toUpperCase();
  if (src) {
    return <img src={src} alt={user?.username} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />;
  }
  return (
    <div style={{ width: size, height: size }} className="rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center font-black shrink-0 text-xs">
      {letter}
    </div>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [adjustFor, setAdjustFor] = useState(null);
  const [adjustKind, setAdjustKind] = useState("credits"); // credits | cash
  const [adjustAmount, setAdjustAmount] = useState("");
  const [reason, setReason] = useState("");
  const [sitesFor, setSitesFor] = useState(null);   // user whose websites are being viewed
  const [sitesList, setSitesList] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [planOptions, setPlanOptions] = useState([]); // available plan names
  // --- Email one user ---
  const [emailFor, setEmailFor] = useState(null);
  const [emailTemplate, setEmailTemplate] = useState("");
  const [emailNote, setEmailNote] = useState("");
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [emailSending, setEmailSending] = useState(false);
  // Collections modal — Diamond Emeralds + Heart Shaped Chrysolite totals +
  // full acquisition history for a specific user.
  const [collectionsFor, setCollectionsFor] = useState(null);
  const [collectionsData, setCollectionsData] = useState(null);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const PER_PAGE = 15;

  const load = async (goPage = page, goSearch = search) => {
    const params = new URLSearchParams({ page: String(goPage), per_page: String(PER_PAGE) });
    if (goSearch) params.set("search", goSearch);
    const { data } = await api.get(`/admin/users?${params.toString()}`);
    setUsers(data.users || []);
    setTotalPages(data.total_pages || 1);
    setTotalCount(data.total || 0);
    setPage(data.page || goPage);
  };
  useEffect(() => {
    load(1, search);
    // Fetch active plan names so the membership dropdown always mirrors reality.
    api.get("/plans").then((r) => {
      const names = (r.data || []).filter((p) => p.active !== false).map((p) => p.name);
      // "Free" is always available even if no explicit Free plan exists.
      setPlanOptions(Array.from(new Set(["Free", ...names])));
    }).catch(() => setPlanOptions(["Free"]));
    // Load available email templates + whether email is configured.
    api.get("/admin/email/templates").then((r) => {
      setEmailTemplates(r.data.templates || []);
      setEmailConfigured(!!r.data.configured);
      if (r.data.templates?.length) setEmailTemplate(r.data.templates[0].key);
    }).catch(() => setEmailConfigured(false));
  }, []);

  const openEmail = (u) => {
    if (!u.email) { toast.error("This user has no email on file"); return; }
    setEmailFor(u);
    setEmailNote("");
    if (emailTemplates.length && !emailTemplate) setEmailTemplate(emailTemplates[0].key);
  };

  const openCollections = async (u) => {
    setCollectionsFor(u);
    setCollectionsData(null);
    setCollectionsLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/collections?limit=200`);
      setCollectionsData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setCollectionsLoading(false);
    }
  };
  const sendEmail = async () => {
    if (!emailFor || !emailTemplate) return;
    setEmailSending(true);
    try {
      await api.post(`/admin/users/${emailFor.id}/email`, { template: emailTemplate, note: emailNote });
      toast.success(`Email sent to ${emailFor.email}`);
      setEmailFor(null); setEmailNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setEmailSending(false);
    }
  };

  const applySearch = (e) => {
    e?.preventDefault?.();
    load(1, search);
  };

  const copyAllEmails = async () => {
    try {
      const { data } = await api.get(`/admin/users/emails`);
      await navigator.clipboard.writeText(data.csv || "");
      toast.success(`Copied ${data.count} email${data.count === 1 ? "" : "s"} to clipboard`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const downloadEmailsCsv = () => {
    const url = `${api.defaults.baseURL}/admin/users/emails?format=csv`;
    window.open(url, "_blank");
  };

  const toggle = async (u) => {
    await api.post(`/admin/users/${u.id}/toggle-active`);
    toast.success(u.is_active ? "User disabled" : "User enabled");
    load();
  };

  const toggleVerified = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/toggle-verified`);
      toast.success(data.verified ? `@${u.username} is now verified` : `Removed verification from @${u.username}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const remove = async (u) => {
    if (u.id === me?.id) return toast.error("You cannot delete yourself");
    if (u.role === "admin") return toast.error("Cannot delete other admins");
    const answer = window.prompt(`Type the username "${u.username}" to permanently delete this account and all their content:`);
    if (answer !== u.username) {
      if (answer !== null) toast.error("Username did not match — deletion cancelled");
      return;
    }
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted @${u.username}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const openAdjust = (u, kind) => {
    setAdjustFor(u);
    setAdjustKind(kind);
    setAdjustAmount("");
    setReason("");
  };

  const openSites = async (u) => {
    setSitesFor(u);
    setSitesList([]);
    setSitesLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/websites`);
      setSitesList(data.websites || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSitesLoading(false);
    }
  };

  const adjust = async () => {
    if (!adjustFor) return;
    try {
      let endpoint;
      if (adjustKind === "cash") endpoint = "adjust-cash";
      else if (adjustKind === "banners") endpoint = "adjust-banner-impressions";
      else if (adjustKind === "textads") endpoint = "adjust-text-ad-impressions";
      else endpoint = "adjust-credits";
      const payload = (adjustKind === "banners" || adjustKind === "textads")
        ? { amount: parseInt(adjustAmount, 10), reason }
        : { amount: parseFloat(adjustAmount), reason };
      await api.post(`/admin/users/${adjustFor.id}/${endpoint}`, payload);
      const label = adjustKind === "cash" ? "Cash" : adjustKind === "banners" ? "Banner impressions" : adjustKind === "textads" ? "Text ad impressions" : "Credits";
      toast.success(`${label} adjusted`);
      setAdjustFor(null); setAdjustAmount(""); setReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const setMembership = async (u, newPlan) => {
    if (!newPlan || newPlan === u.membership) return;
    const bonusMsg = newPlan === "Free" ? "" : "\n\nThe plan's credit bonus will be added to their balance automatically.";
    const ok = window.confirm(`Change @${u.username}'s membership to ${newPlan}?${bonusMsg}`);
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/set-membership`, {
        membership: newPlan,
        credit_bonus: true,
      });
      toast.success(
        data.credit_bonus_awarded
          ? `@${u.username} → ${data.membership} (+${data.credit_bonus_awarded} credits)`
          : `@${u.username} → ${data.membership}`
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const pageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const window = new Set([1, totalPages, page, page - 1, page + 1]);
    if (page <= 3) [2, 3, 4].forEach((n) => window.add(n));
    if (page >= totalPages - 2) [totalPages - 1, totalPages - 2, totalPages - 3].forEach((n) => window.add(n));
    return Array.from(window).filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Users</h1>
          <div className="text-xs text-slate-500 mt-1">{totalCount} total</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="admin-copy-emails-btn"
            onClick={copyAllEmails}
            className="inline-flex items-center gap-1 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold px-4 py-2 transition-colors duration-200"
            title="Copy all user emails as a comma-separated list"
          >
            <Envelope size={14} weight="bold" /> Copy emails
          </button>
          <button
            data-testid="admin-download-emails-csv"
            onClick={downloadEmailsCsv}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm font-bold px-4 py-2 transition-colors duration-200"
            title="Download all user emails as a CSV file"
          >
            <DownloadSimple size={14} weight="bold" /> CSV
          </button>
          <form onSubmit={applySearch} className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="admin-users-search" placeholder="Search name, username, email…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 pr-3 py-2 w-64 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100" />
            </div>
            <button type="submit" data-testid="admin-users-search-btn" className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold px-3 py-2">Search</button>
          </form>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[880px]">
          <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3 text-left">Username</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Role</th>
              <th className="p-3 text-right">Credits</th>
              <th className="p-3 text-right">Cash&nbsp;($)</th>
              <th className="p-3 text-right" title="Banner impressions balance">Banners</th>
              <th className="p-3 text-right" title="Text ad impressions balance">Text&nbsp;ads</th>
              <th className="p-3 text-right" title="Total sites surfed">Surfs</th>
              <th className="p-3 text-left">Plan</th>
              <th className="p-3 text-center">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {users.map((u) => {
              const canDelete = u.id !== me?.id && u.role !== "admin";
              return (
              <tr key={u.id} data-testid={`user-row-${u.username}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <td className="p-3">
                  <AdminUserSitesHoverCard user={u}>
                    <Link to={`/u/${u.id}`} className="flex items-center gap-2 hover:underline" data-testid={`admin-user-link-${u.username}`}>
                      <UserAvatar user={u} size={32} />
                      <span className="font-semibold text-slate-900 dark:text-white inline-flex items-center gap-1">
                        @{u.username}
                        <UserBadge user={u} size={14} />
                        <span className="ml-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 inline-flex items-center gap-0.5" data-testid={`user-surfs-${u.username}`} title="Total sites surfed">
                          <MouseSimple size={10} weight="bold" /> {u.total_surfs ?? 0}
                        </span>
                      </span>
                    </Link>
                  </AdminUserSitesHoverCard>
                </td>
                <td className="p-3 text-slate-600 dark:text-slate-300">{u.email}</td>
                <td className="p-3"><span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full ${u.role === "admin" ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300" : "bg-slate-100 text-slate-600 dark:bg-slate-800"}`}>{u.role}</span></td>
                <td className="p-3 text-right font-black text-slate-900 dark:text-white">{Number(u.credits || 0).toFixed(1)}</td>
                <td className="p-3 text-right font-black text-emerald-600 dark:text-emerald-400">${Number(u.cash_wallet || 0).toFixed(2)}</td>
                <td className="p-3 text-right">
                  <span
                    data-testid={`user-banner-impressions-${u.username}`}
                    className="inline-flex items-center gap-1 rounded-full bg-fuchsia-100 dark:bg-fuchsia-500/20 text-fuchsia-700 dark:text-fuchsia-300 text-[11px] font-black px-2 py-0.5"
                    title="Banner impressions balance"
                  >
                    <Flag size={10} weight="fill" /> {Number(u.banner_impressions || 0).toLocaleString()}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <span
                    data-testid={`user-text-ad-impressions-${u.username}`}
                    className="inline-flex items-center gap-1 rounded-full bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 text-[11px] font-black px-2 py-0.5"
                    title="Text ad impressions balance"
                  >
                    <Flag size={10} weight="fill" /> {Number(u.text_ad_impressions || 0).toLocaleString()}
                  </span>
                </td>
                <td className="p-3 text-right font-black text-slate-900 dark:text-white">{u.total_surfs ?? 0}</td>
                <td className="p-3">
                  <select
                    data-testid={`membership-select-${u.username}`}
                    value={planOptions.includes(u.membership) ? u.membership : (u.membership || "Free")}
                    onChange={(e) => setMembership(u, e.target.value)}
                    disabled={u.role === "admin"}
                    className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1 text-xs font-bold text-slate-900 dark:text-slate-100 disabled:opacity-60 disabled:cursor-not-allowed hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title={u.role === "admin" ? "Cannot change an admin's plan" : "Change membership plan"}
                  >
                    {!planOptions.includes(u.membership) && u.membership && (
                      <option value={u.membership}>{u.membership}</option>
                    )}
                    {planOptions.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </td>
                <td className="p-3 text-center"><span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full ${u.is_active ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300" : "bg-red-100 text-red-700"}`}>{u.is_active ? "active" : "disabled"}</span></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button data-testid={`adjust-credits-${u.username}`} onClick={() => openAdjust(u, "credits")} className="text-blue-700 dark:text-blue-400 text-xs font-bold hover:underline mr-2">± credits</button>
                  <button data-testid={`adjust-cash-${u.username}`} onClick={() => openAdjust(u, "cash")} className="text-emerald-600 dark:text-emerald-400 text-xs font-bold hover:underline mr-2">± cash</button>
                  <button data-testid={`adjust-banners-${u.username}`} onClick={() => openAdjust(u, "banners")} className="text-fuchsia-600 dark:text-fuchsia-400 text-xs font-bold hover:underline mr-2">± banners</button>
                  <button data-testid={`adjust-textads-${u.username}`} onClick={() => openAdjust(u, "textads")} className="text-cyan-600 dark:text-cyan-400 text-xs font-bold hover:underline mr-2">± text ads</button>
                  <button
                    data-testid={`email-user-${u.username}`}
                    onClick={() => openEmail(u)}
                    disabled={!emailConfigured}
                    title={emailConfigured ? "Send this user a transactional email" : "Email is not configured (missing EMERGENT_EMAIL_KEY)"}
                    className={`inline-flex items-center gap-1 text-xs font-bold mr-2 ${emailConfigured ? "text-indigo-600 dark:text-indigo-400 hover:underline" : "text-slate-400 cursor-not-allowed"}`}
                  >
                    <Envelope size={12} weight="bold" /> email
                  </button>
                  <button
                    data-testid={`view-sites-${u.username}`}
                    onClick={() => openSites(u)}
                    title={`See websites submitted by @${u.username}`}
                    className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-blue-100 hover:text-blue-700 dark:hover:bg-blue-500/20 dark:hover:text-blue-300 text-slate-500 inline-flex items-center justify-center mr-1 transition-colors duration-200"
                  >
                    <Globe size={16} weight="bold" />
                  </button>
                  <button
                    data-testid={`view-collections-${u.username}`}
                    onClick={() => openCollections(u)}
                    title={`Diamond Emeralds + Chrysolite for @${u.username}`}
                    className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-emerald-100 hover:text-emerald-700 dark:hover:bg-emerald-500/20 dark:hover:text-emerald-300 text-slate-500 inline-flex items-center justify-center mr-1 transition-colors duration-200"
                  >
                    <Diamond size={16} weight="fill" />
                  </button>
                  <button
                    data-testid={`toggle-verified-${u.username}`}
                    onClick={() => toggleVerified(u)}
                    title={u.verified ? "Remove verification" : "Grant blue check"}
                    className={`h-8 w-8 rounded-full inline-flex items-center justify-center mr-1 transition-colors duration-200 ${u.verified ? "bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-500/20" : "bg-slate-100 text-slate-400 hover:bg-slate-200 dark:bg-slate-800"}`}
                  >
                    <SealCheck size={16} weight={u.verified ? "fill" : "bold"} />
                  </button>
                  <button data-testid={`toggle-${u.username}`} onClick={() => toggle(u)} className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 inline-flex items-center justify-center mr-1" title={u.is_active ? "Disable" : "Enable"}>
                    {u.is_active ? <ToggleRight size={20} weight="fill" className="text-green-600" /> : <ToggleLeft size={20} weight="fill" className="text-slate-400" />}
                  </button>
                  <button
                    data-testid={`delete-${u.username}`}
                    onClick={() => remove(u)}
                    disabled={!canDelete}
                    title={canDelete ? "Delete user" : "Cannot delete self or admins"}
                    className="h-8 w-8 rounded-full inline-flex items-center justify-center bg-red-100 hover:bg-red-500 hover:text-white text-red-600 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-red-100 disabled:hover:text-red-600 transition-colors duration-200"
                  >
                    <Trash size={16} weight="bold" />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="mt-4 flex items-center justify-center gap-1 flex-wrap" data-testid="admin-users-pagination">
          <button
            data-testid="admin-users-prev"
            disabled={page <= 1}
            onClick={() => load(page - 1, search)}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <CaretLeft size={14} weight="bold" /> Prev
          </button>
          {pageNumbers().map((n, i, arr) => (
            <span key={n} className="inline-flex items-center">
              {i > 0 && arr[i - 1] < n - 1 && <span className="px-1 text-slate-400">…</span>}
              <button
                data-testid={`admin-users-page-${n}`}
                onClick={() => load(n, search)}
                className={`h-9 w-9 rounded-full text-sm font-bold transition-colors duration-200 ${n === page ? "bg-blue-700 text-white" : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
              >{n}</button>
            </span>
          ))}
          <button
            data-testid="admin-users-next"
            disabled={page >= totalPages}
            onClick={() => load(page + 1, search)}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
          >
            Next <CaretRight size={14} weight="bold" />
          </button>
        </nav>
      )}

      {adjustFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setAdjustFor(null)}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-black text-lg text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
              Adjust {adjustKind === "cash" ? "cash wallet" : adjustKind === "banners" ? "banner impressions" : adjustKind === "textads" ? "text ad impressions" : "credits"} — @{adjustFor.username}
            </h2>
            <p className="text-xs text-slate-500 mt-1">Positive to add, negative to subtract{adjustKind === "cash" ? " (USD)" : (adjustKind === "banners" || adjustKind === "textads") ? " (whole impressions)" : ""}.</p>
            <input data-testid="adjust-amount-input" type="number" step={adjustKind === "cash" ? "0.01" : (adjustKind === "banners" || adjustKind === "textads") ? "1" : "0.1"} placeholder={adjustKind === "cash" ? "Amount in USD (e.g. 5.00)" : (adjustKind === "banners" || adjustKind === "textads") ? "Impressions (e.g. 10000)" : "Amount (e.g. 100)"} value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} className="w-full mt-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
            <input data-testid="adjust-reason-input" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full mt-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
            <div className="mt-3 flex gap-2 justify-end">
              <button onClick={() => setAdjustFor(null)} className="rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 px-4 py-2 text-sm">Cancel</button>
              <button data-testid="adjust-confirm-btn" onClick={adjust} className="rounded-full bg-blue-700 hover:bg-blue-800 text-white font-bold px-4 py-2 text-sm transition-colors duration-200">Apply</button>
            </div>
          </div>
        </div>
      )}
      {sitesFor && (
        <div
          data-testid="admin-sites-modal"
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSitesFor(null)}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-2xl p-5 max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h2
                  className="font-black text-lg text-slate-900 dark:text-white"
                  style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
                  data-testid="admin-sites-modal-title"
                >
                  Websites submitted by @{sitesFor.username}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">{sitesFor.email}</p>
              </div>
              <button
                data-testid="admin-sites-modal-close"
                onClick={() => setSitesFor(null)}
                className="rounded-full h-8 w-8 inline-flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-500"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto flex-1 -mx-1 px-1">
              {sitesLoading && (
                <div className="text-sm text-slate-500 text-center py-8">Loading…</div>
              )}
              {!sitesLoading && sitesList.length === 0 && (
                <div
                  data-testid="admin-sites-modal-empty"
                  className="rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 p-6 text-center text-sm text-slate-500"
                >
                  @{sitesFor.username} hasn&rsquo;t submitted any websites yet.
                </div>
              )}
              {!sitesLoading && sitesList.length > 0 && (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {sitesList.map((s) => (
                    <li
                      key={s.id}
                      data-testid={`admin-user-site-${s.id}`}
                      className="py-3 flex items-center gap-3 flex-wrap"
                    >
                      <div className="flex-1 min-w-[220px]">
                        <div className="font-bold text-slate-900 dark:text-white text-sm truncate">
                          {s.title || s.url}
                        </div>
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-700 dark:text-blue-400 hover:underline break-all"
                        >
                          {s.url}
                        </a>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">
                          {s.category || "site"} · {s.status}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-center">
                          <div className="text-[10px] uppercase tracking-widest text-slate-500">
                            Credits
                          </div>
                          <div
                            className="font-black text-slate-900 dark:text-white"
                            data-testid={`admin-user-site-credits-${s.id}`}
                          >
                            {Number(s.credits_assigned || 0).toFixed(0)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-[10px] uppercase tracking-widest text-slate-500">
                            Visits
                          </div>
                          <div
                            className="font-black text-slate-900 dark:text-white"
                            data-testid={`admin-user-site-visits-${s.id}`}
                          >
                            {Number(s.views_earned || 0).toFixed(0)}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {emailFor && (
        <div
          data-testid="email-user-modal"
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !emailSending && setEmailFor(null)}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-lg w-full space-y-3 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h2 className="font-black text-lg text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
                Email @{emailFor.username}
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Sending to <span className="font-bold">{emailFor.email}</span>. One recipient at a time — this is not a broadcast tool.
              </p>
            </div>

            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Template</label>
              <select
                data-testid="email-template-select"
                value={emailTemplate}
                onChange={(e) => setEmailTemplate(e.target.value)}
                className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
              >
                {emailTemplates.map((t) => (
                  <option key={t.key} value={t.key}>{t.label} — “{t.subject}”</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Personal note (optional)</label>
              <textarea
                data-testid="email-note-input"
                rows={4}
                placeholder="Add a short paragraph the recipient will see below the greeting…"
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value.slice(0, 800))}
                className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
              />
              <div className="text-[10px] text-slate-400 mt-1 text-right">{emailNote.length} / 800</div>
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-[11px] text-slate-500 dark:text-slate-400">
              Emails are sent from the platform-managed shared domain. Never ask for passwords, codes or payment details in the note — those emails will be rejected.
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                data-testid="email-cancel-btn"
                onClick={() => setEmailFor(null)}
                disabled={emailSending}
                className="px-4 py-2 text-sm text-slate-700 dark:text-slate-300 font-bold hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                data-testid="email-send-btn"
                onClick={sendEmail}
                disabled={emailSending || !emailTemplate}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-black uppercase tracking-widest rounded-full px-4 py-2 text-sm"
              >
                {emailSending ? "Sending…" : "Send email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collections / Prizes modal — Diamond Emeralds + Heart Shaped
          Chrysolite + Surf Drawn Tickets totals with the full acquisition
          history for the selected user. */}
      {collectionsFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={() => setCollectionsFor(null)} />
          <div className="relative w-full max-w-2xl rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
            <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200 dark:border-slate-800">
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Collections / Prizes</div>
                <div className="text-xl font-black text-slate-900 dark:text-white truncate" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>@{collectionsFor.username}</div>
                <div className="text-xs text-slate-500 truncate">{collectionsFor.email}</div>
              </div>
              <button
                data-testid="collections-close"
                onClick={() => setCollectionsFor(null)}
                className="rounded-full w-8 h-8 inline-flex items-center justify-center bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300"
              >
                <X size={14} />
              </button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {collectionsLoading && <div className="text-sm text-slate-500 text-center py-6">Loading…</div>}
              {!collectionsLoading && collectionsData && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div data-testid="collections-tickets" className="rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white p-4">
                      <div className="flex items-center justify-between">
                        <Ticket size={20} weight="fill" className="opacity-90" />
                        <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Tickets</div>
                      </div>
                      <div className="mt-2 text-3xl font-black" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{Number(collectionsData.totals?.surf_drawn_tickets || 0).toLocaleString()}</div>
                    </div>
                    <div data-testid="collections-diamonds" className="rounded-2xl bg-gradient-to-br from-emerald-500 to-green-700 text-white p-4">
                      <div className="flex items-center justify-between">
                        <Diamond size={20} weight="fill" className="opacity-90" />
                        <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Diamonds</div>
                      </div>
                      <div className="mt-2 text-3xl font-black" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{Number(collectionsData.totals?.diamond_emeralds || 0).toLocaleString()}</div>
                    </div>
                    <div data-testid="collections-chrysolite" className="rounded-2xl bg-gradient-to-br from-rose-500 to-pink-700 text-white p-4">
                      <div className="flex items-center justify-between">
                        <Heart size={20} weight="fill" className="opacity-90" />
                        <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Chrysolite</div>
                      </div>
                      <div className="mt-2 text-3xl font-black" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{Number(collectionsData.totals?.heart_chrysolite || 0).toLocaleString()}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">Acquisition history</div>
                    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                      {(collectionsData.history?.items || []).length === 0 && (
                        <div className="p-6 text-center text-sm text-slate-500">Nothing collected yet.</div>
                      )}
                      {(collectionsData.history?.items || []).map((h, i) => {
                        const positive = Number(h.amount) >= 0;
                        return (
                          <div key={i} className="p-3 flex items-center gap-3 text-sm">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${positive ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"}`}>
                              {h.type === "diamond_emeralds" ? <Diamond size={14} weight="fill" /> : h.type === "heart_chrysolite" ? <Heart size={14} weight="fill" /> : h.type === "surf_drawn_tickets" ? <Ticket size={14} weight="fill" /> : <PlusCircle size={14} weight="fill" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`font-bold truncate ${positive ? "text-slate-900 dark:text-white" : "text-red-600 dark:text-red-400"}`}>
                                {positive ? "+" : ""}{Number(h.amount).toLocaleString()} {h.type_label}
                              </div>
                              <div className="text-xs text-slate-500 capitalize">{new Date(h.created_at).toLocaleString()} · {h.source_label}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-2 text-right">Showing up to {collectionsData.history?.limit || 200} of {collectionsData.history?.total || 0}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
